import { useGQLQuery } from "@hoppscotch/common/composables/graphql"
import { TeamName } from "@helpers/backend/types/TeamName"
import { getService } from "@hoppscotch/common/modules/dioc"
import { BackendPlatformDef } from "@hoppscotch/common/platform/backend"

import { runGQLQuery, runMutation } from "@helpers/backend/GQLClient"
import { HoppRESTRequest } from "@hoppscotch/data"
import { Email } from "@app/helpers/admin/Email"

import {
  AcceptTeamInvitationDocument,
  AcceptTeamInvitationMutation,
  AcceptTeamInvitationMutationVariables,
  CreateShortcodeDocument,
  CreateShortcodeMutation,
  CreateShortcodeMutationVariables,
  CreateTeamDocument,
  CreateTeamInvitationDocument,
  CreateTeamInvitationMutation,
  CreateTeamInvitationMutationVariables,
  CreateTeamMutation,
  CreateTeamMutationVariables,
  GetInviteDetailsDocument,
  GetInviteDetailsQuery,
  GetInviteDetailsQueryVariables,
  GetMyTeamsDocument,
  GetMyTeamsQuery,
  GetMyTeamsQueryVariables,
  GetUserShortcodesDocument,
  GetUserShortcodesQuery,
  GetUserShortcodesQueryVariables,
  TeamMemberRole,
} from "@app/helpers/backend/graphql"
import { OrganizationService } from "@app/services/organization.service"

const getInviteDetails = <GetInviteDetailsError extends string>(
  inviteID: string
) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return useGQLQuery<
    GetInviteDetailsQuery,
    GetInviteDetailsQueryVariables,
    GetInviteDetailsError
  >({
    query: GetInviteDetailsDocument,
    variables: {
      inviteID,
      orgID,
    },
    defer: true,
  })
}

const getUserShortcodes = (cursor?: string) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return runGQLQuery<
    GetUserShortcodesQuery,
    GetUserShortcodesQueryVariables,
    ""
  >({
    query: GetUserShortcodesDocument,
    variables: {
      cursor,
      orgID,
    },
  })
}

const getUserTeams = (cursor?: string, matchAllTeams?: boolean) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return runGQLQuery<GetMyTeamsQuery, GetMyTeamsQueryVariables, "">({
    query: GetMyTeamsDocument,
    variables: {
      cursor,
      ...(matchAllTeams ? {} : { orgID }),
    },
  })
}

export const createTeam = <CreateTeamErrors extends string>(name: TeamName) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return runMutation<
    CreateTeamMutation,
    CreateTeamMutationVariables,
    CreateTeamErrors
  >(CreateTeamDocument, {
    name,
    orgID,
  })
}

export const createTeamInvitation = <CreateTeamInvitationErrors extends string>(
  inviteeEmail: Email,
  inviteeRole: TeamMemberRole,
  teamID: string
) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return runMutation<
    CreateTeamInvitationMutation,
    CreateTeamInvitationMutationVariables,
    CreateTeamInvitationErrors
  >(CreateTeamInvitationDocument, {
    inviteeEmail,
    inviteeRole,
    teamID,
    orgID,
  })
}

export const acceptTeamInvitation = <AcceptTeamInvitationErrors extends string>(
  inviteID: string
) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return runMutation<
    AcceptTeamInvitationMutation,
    AcceptTeamInvitationMutationVariables,
    AcceptTeamInvitationErrors
  >(AcceptTeamInvitationDocument, {
    inviteID,
    orgID,
  })
}

export const createShortcode = (
  request: HoppRESTRequest,
  properties?: string
) => {
  const organizationService = getService(OrganizationService)
  const orgID = organizationService.getOrganizationID()

  return runMutation<
    CreateShortcodeMutation,
    CreateShortcodeMutationVariables,
    ""
  >(CreateShortcodeDocument, {
    request: JSON.stringify(request),
    properties,
    orgID,
  })
}

export const def: BackendPlatformDef = {
  getInviteDetails,
  getUserShortcodes,
  getUserTeams,
  createTeam,
  createTeamInvitation,
  acceptTeamInvitation,
  createShortcode,
}
